import AppEvents from 'Shared/app-events';
import Aside from './aside';

export default class BarcodeScanner extends Aside
{
    constructor(options = {
        id: "aside-scanner",
        scannerId: "barcode-scanner"
    })
    {
        super(options);

        this.offcanvasEl.addEventListener('shown.bs.offcanvas', () => {
            this.startCamera(options);
        });

        this.offcanvasEl.addEventListener('hidden.bs.offcanvas', () => {
            if (this.scanner) {
                this.stopCamera();
            }

            this.#deferred.reject();
            this.#deferred = null;
        });

        AppEvents.on("scan", (args) => {
            this.scan().done(args.success);
        });
    }

    #Html5QrcodeSupportedFormats;
    #Html5QrcodeScannerState;
    #Html5Qrcode;

    cameras = ko.observableArray([]);
    #camerasLoaded = false;

    get #selectedCamera() {
        return localStorage?.getItem("camera-device");
    }
    set #selectedCamera(value) {
        localStorage?.setItem("camera-device", value);
    }

    scanner = null;

    async #initScanner() {
        const constraints = {
            audio: false,
            video: true,
        };

        await navigator.mediaDevices.getUserMedia(constraints);

        const { Html5QrcodeSupportedFormats, Html5QrcodeScannerState, Html5Qrcode } = await import(/* webpackChunkName: "html5-qrcode" */ 'html5-qrcode');

        this.#Html5QrcodeSupportedFormats = Html5QrcodeSupportedFormats;
        this.#Html5QrcodeScannerState = Html5QrcodeScannerState;
        this.#Html5Qrcode = Html5Qrcode;

        let config = {
            formatsToSupport: [
                Html5QrcodeSupportedFormats.EAN_13,
                Html5QrcodeSupportedFormats.CODE_128,
                Html5QrcodeSupportedFormats.DATA_MATRIX,
                Html5QrcodeSupportedFormats.QR_CODE,
            ],
            useBarCodeDetectorIfSupported: true,
            verbose: true
        };

        this.scanner = new Html5Qrcode(this.options.scannerId, config);
    }

    async #initCameras()
    {
        try {
            var cameras = await this.#Html5Qrcode.getCameras();

            if (!cameras) {
                this.cameras(null);
                return;
            }

            this.cameras(cameras);

            let camera;

            const selectedCamera = this.#selectedCamera;

            if (selectedCamera) {
                camera = cameras.find(item => item.id == selectedCamera);
            }

            if (!camera) {
                camera = cameras.find(x => x.label.toLowerCase().includes("environment") || x.label.toLowerCase().includes("back"));
            }

            if (!camera) {
                camera = cameras[0];
            }

            this.#selectedCamera = camera.id;

            this.#camerasLoaded = true;

        } catch (err) {
            console.error(err);
            this.errorMessage(err);
            throw err;
        }
    }

    #startLoading = () => this.loading(true);
    #stopLoading = () => this.loading(false);

    started = ko.observable(false);

    torchEnabled = ko.observable(false);
    torchSupported = ko.observable(false);

    enableTorch = async () => {
        if (this.scanner && this.scanner.getState() === this.#Html5QrcodeScannerState.SCANNING) {
            const torchSupported = this.torchSupported();
            if (!torchSupported) {
                console.warn("Torch is not supported by this camera.");
                return;
            }

            const enabled = this.torchEnabled();

            await this.scanner.applyVideoConstraints({
                torch: !enabled
            });

            this.torchEnabled(!enabled);
        }
    }

    async startCamera(options) {

        this.options = options || this.options;
        try {
            if (!this.scanner) {
                await this.#initScanner();
            }

            if (!this.#camerasLoaded) {
                await this.#initCameras();
            }

            switch (this.scanner.getState()) {
                case this.#Html5QrcodeScannerState.NOT_STARTED:
                    //const dpr = window.devicePixelRatio || 1;
                    let scanConfig = {
                        fps: 10,
                        qrbox: {
                            height: 200,
                            width: 250
                        },
                        aspectRatio: 4 / 3,
                        disableFlip: true
                    };

                    const cameraId = this.#selectedCamera;

                    await this.scanner.start(
                        {
                            deviceId: cameraId
                        },
                        scanConfig,
                        this.#onScanSuccess,
                        this.#onScanFail
                    )

                    const caps = this.scanner.getRunningTrackCapabilities();

                    if (caps.focusMode) {
                        this.scanner.applyVideoConstraints({
                            focusMode: "continuous"
                        });
                    }

                    if (caps.zoom && caps.zoom.max >= 2) {
                        await this.#setZoom(2);
                    }

                    this.torchSupported(caps.torch);

                    break;
                case this.#Html5QrcodeScannerState.PAUSED:
                    this.scanner.resume();
                    break;
                default:
                    break;
            }
        }
        finally {
            this.#stopLoading();
        }
    }

    async stopCamera() {
        switch (this.scanner.getState()) {
            case this.#Html5QrcodeScannerState.SCANNING:
            case this.#Html5QrcodeScannerState.PAUSED:
                await this.scanner.stop();
                break;
            default:
                break;
        }
    }

    async #isZoomSupported() {
        try {
            console.log(this.scanner.getRunningTrackCapabilities());
            const capabilities = this.scanner.getRunningTrackCapabilities();
            return capabilities?.zoom !== undefined && capabilities.zoom.max >= 2;
        } catch (err) {
            console.debug("Error checking zoom support:", err);
            return false;
        }
    }

    async #setZoom(level) {
        if (this.scanner && this.scanner.getState() === this.#Html5QrcodeScannerState.SCANNING) {
            const zoomSupported = await this.#isZoomSupported();
            if (!zoomSupported) {
                console.debug("Zoom is not supported by this camera.");
                return;
            }

            await this.scanner.applyVideoConstraints({
                advanced: [{ zoom: level }]
            });
        }
    }

    async #switchCamera() {
        this.switching(true);
        await this.stopCamera();

        const cameras = this.cameras();

        let index = cameras.findIndex(item => item.id == this.#selectedCamera) ?? 0;
        index++;

        if (index >= cameras.length) {
            index = 0;
        }

        this.#selectedCamera = cameras[index].id;

        await this.startCamera();
        this.switching(false);
    }

    hasNext = ko.pureComputed(() => this.cameras().length > 1);

    nextCamera = () => {
        this.#switchCamera();
    }

    pauseCamera = () => {
        if (this.scanner && this.scanner.getState() === this.#Html5QrcodeScannerState.SCANNING) {
            this.scanner.pause(true);
        }
    }

    resumeCamera = () => {
        if (this.scanner && this.scanner.getState() === this.#Html5QrcodeScannerState.PAUSED) {
            this.scanner.resume(true);
        }
    }

    switching = ko.observable(false);
    loading = ko.observable(true);
    errorMessage = ko.observable("");

    #onScanSuccess = (decodedText, result) => {
        this.#deferred.resolve(decodedText);
        this.hide();
    };

    #onScanFail(errorMessage) {
        // ignore
    };

    #deferred;

    scan = () => {
        this.show();

        this.#deferred = $.Deferred();

        return this.#deferred;
    }
}